import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import HowItWorks from '../content/HowItWorks';
import CateringCta from '../content/CateringCta';
import AnyEvent from '../content/AnyEvent';
import Testimonials from '../content/Testimonials';
import WeddingFaq from '../faqs/WeddingFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const getWeddingEvents = (vehicleType, cityName) => [
  {
    title: 'Wedding Reception Catering',
    text: `Serve a fresh, unforgettable meal with ${cityName}'s best wedding food ${vehicleType}s. From tacos to wood-fired pizza, food ${vehicleType}s deliver made-to-order dishes your guests will love.`,
    icon: 'https://static.seattlefoodtruck.com/badges/weddingrings.svg'
  },
  {
    title: 'Cocktail Hour Appetizers',
    text: 'Kick things off with small bites and street food-style starters. Mini sliders, bao, dumplings, or poke bowls are perfect for guests to enjoy while mingling during your cocktail hour.',
    icon: 'https://static.seattlefoodtruck.com/badges/bubbles.svg'
  },
  {
    title: 'Rehearsal Dinner Catering',
    text: `Set the tone for your wedding weekend with a laid-back rehearsal dinner. Food ${vehicleType}s make it easy to serve great food, accommodate different tastes, and create a welcoming, low-pressure vibe for your closest guests.`,
    icon: 'https://static.seattlefoodtruck.com/badges/most-booked.webp',
    width: 'lg'
  },
  {
    title: 'Late-Night Wedding Snacks',
    text: `Keep the energy high with surprise late-night snacks. From tacos and grilled cheese to fresh donuts or fries, food ${vehicleType}s offer fun, craveable bites for the dance floor crowd.`,
    icon: 'https://static.seattlefoodtruck.com/badges/party.svg'
  },
  {
    title: 'Wedding Dessert Trucks',
    text: `Make dessert extra special with a food ${vehicleType} serving ice cream, cupcakes, churros, or waffles. It's a sweet way to end the night and a crowd favorite for guests of all ages.`,
    icon: '/static/images/admin/catering/birthday.svg',
    width: 'sm'
  },
  {
    title: 'Day-After Brunch Catering',
    text: `Say thank you with a post-wedding brunch food ${vehicleType}. Breakfast burritos, coffee, waffles, and bagels make for a casual, feel-good send-off before guests head home.`,
    icon: 'https://static.seattlefoodtruck.com/badges/truck.webp',
    width: 'lg'
  },
  {
    title: 'Elopements & Micro Weddings',
    text: `Planning something small and intimate? Food ${vehicleType}s provide personalized, high-quality catering that fits perfectly with elopements and micro weddings in and around ${cityName}.`,
    icon: 'https://static.seattlefoodtruck.com/badges/mask.svg'
  },
  {
    title: 'Venue-Friendly Service',
    text: `From rooftop venues to vineyards, parks, or private homes, our ${vehicleType}s are equipped to serve at nearly any ${cityName} wedding location—no kitchen or indoor setup required.`,
    icon: 'https://static.seattlefoodtruck.com/badges/sft-favs.webp',
    width: 'lg'
  }
];

function WeddingCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const weddingEvents = getWeddingEvents(vehicleType, city_name);

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type} Wedding Catering`}
        type='website'
        canonical='/food-truck-catering/wedding-catering'
        image='https://static.seattlefoodtruck.com/photos/catering5.webp'
        description={`Book food ${vehicleType} wedding catering in ${city_name}. From receptions to late-night snacks, compare top food ${vehicleType}s and find the perfect fit for your big day.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Wedding Catering in ${city_name}`,
            description: `Book food ${vehicleType} wedding catering in ${city_name}. Receptions, rehearsal dinners, late-night snacks and more.`,
            url: '/food-truck-catering/wedding-catering',
            serviceType: 'Food Truck Wedding Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Wedding Catering', item: '/food-truck-catering/wedding-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/wedding-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>The Best Way to Book a Food {vehicle_type} for Your Wedding</h1>
            <p>Fresh, fun, and totally unforgettable — nothing elevates your big day like a food {vehicleType} wedding feast! Whether you&#39;re planning a laid-back backyard ceremony, an elegant vineyard reception, or a modern city celebration, food {vehicleType}s offer a unique and crowd-pleasing catering experience. From gourmet sliders and <Link to='/food-truck-catering/pizza-catering'>artisan pizza</Link> to <Link to='/food-truck-catering/asian-catering'>sushi bars</Link>, <Link to='/food-truck-catering/taco-catering'>tacos</Link>, and <Link to='/food-truck-catering/dessert-catering'>late-night desserts</Link>, {site_title} makes it effortless to find and book the perfect wedding food {vehicleType} in {city_name}. Simply tell us about your wedding, <Link to='/food-truck-catering/prices'>compare pricing</Link>, and lock in your ideal match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Wedding Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <section className='Section'>
        <div className={cn(styles.Cta, styles.Cta__list)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/wedding1.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <div>
              <h3>🎯 Custom Wedding Matches</h3>
              <ul>
                <li>Free, no-obligation quotes tailored to your wedding day.</li>
                <li>Access to {city_name}&#39;s best wedding-ready food {vehicleType}s — from entrées to desserts.</li>
                <li>A fun, easy way to add unforgettable flavor to your big day.</li>
              </ul>
            </div>
            <div>
              <h3>🤝 Helpful Vendor Connections</h3>
              <ul>
                <li>We connect you directly with top-rated food {vehicleType}s — no middlemen or commissions.</li>
                <li>Get quick responses, transparent pricing, and menus that match your vision.</li>
                <li>Have questions? We&#39;re here to help guide the process along the way.</li>
              </ul>
            </div>
            <div>
              <h3>✅ Stress-Free, Reliable Booking</h3>
              <ul>
                <li>Work with trusted food {vehicleType} vendors who&#39;ve catered hundreds of weddings.</li>
                <li>Backup options available in case of cancellations or last-minute changes.</li>
                <li>A secure, low-stress way to book incredible wedding catering in just a few clicks.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FeaturedTrucks />

      <WhyBookWithUs />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What Makes Us Different?</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/wedding2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Unmatched Selection</h4>
            <p>
              {site_title} connects you with {city_name}&#39;s best food {vehicleType}s, offering a variety of cuisines — from wood-fired pizza to fresh seafood to gourmet cupcakes. Whether you&#39;re craving classic al pastor or a gourmet truffle mushroom pizza, we&#39;ve got you covered. Our vendors use high-quality, fresh ingredients, and many offer locally sourced toppings to elevate the taste and experience. Some {vehicleType}s even provide gluten-free and vegan options, ensuring everyone at your event can enjoy a delicious meal.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/wedding3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Seamless Booking Process</h4>
            <p>
              No need to call around or email multiple vendors. Our user-friendly platform allows you to enter your event details, receive multiple quotes from verified food {vehicleType}s, and book in minutes. Every {vehicleType} in our network is vetted to ensure quality, reliability, and excellent customer service. Whether you need an entire catering setup for a large wedding or just a fun dessert option for a small gathering, our process is designed to be as smooth and efficient as possible.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/wedding5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Dedicated Event Support & Flexibility</h4>
            <p>
              We&#39;re not just a booking service — we&#39;re your event partner. Our team is dedicated to helping you create an unforgettable experience, no matter the size or type of event. Need last-minute adjustments? We&#39;ll work with vendors to accommodate changes. Not sure how much food you&#39;ll need? Our team can help estimate portions and recommend the right truck for your guest count. If a vendor has to cancel unexpectedly, we have a network of replacement {vehicleType}s ready to step in so your event never misses a beat.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent
        title={`Ways to Use Food ${vehicleType}s at Your Wedding`}
        events={weddingEvents}
      />

      <Testimonials />

      <CostEstimator title='Wedding Catering Cost Estimator' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <WeddingFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

export default WeddingCatering;
